import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Users,
  Building2,
  ShieldCheck,
  HeartHandshake,
  CreditCard,
  BarChart3,
  MessageCircle,
  CheckCircle2,
  ArrowRight,
  Star,
  ChevronRight,
} from "lucide-react";
import { LandingHeader } from "@/components/LandingHeader";
import { Footer } from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const HomePage = () => {
  const [memberCount, setMemberCount] = useState(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    associationName: "",
    memberEstimate: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState("idle");

  // Reveal scroll progress pour l'animation de la photo (0 → 1)
  const [photoReveal, setPhotoReveal] = useState(0);
  const statsSectionRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/members/count`)
      .then((res) => setMemberCount(res.data.count))
      .catch(() => { });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!statsSectionRef.current) return;
      const statsTop = statsSectionRef.current.offsetTop;
      const p = Math.min(window.scrollY / statsTop, 1);
      setPhotoReveal(p);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleFormChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("loading");
    try {
      await axios.post(`${API}/contact/association-demo`, {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        association_name: form.associationName,
        member_estimate: form.memberEstimate,
        message: form.message,
      });
      setFormStatus("success");
      setForm({ firstName: "", lastName: "", email: "", phone: "", associationName: "", memberEstimate: "", message: "" });
    } catch {
      setFormStatus("error");
    }
  };

  const features = [
    {
      value: "membres",
      icon: Users,
      trigger: "Gérer vos membres",
      content:
        "Créez des profils membres, importez votre liste existante, consultez le statut de chaque adhérent. Une vue d'ensemble claire, actualisée en temps réel.",
    },
    {
      value: "cotisations",
      icon: CreditCard,
      trigger: "Suivre les cotisations",
      content:
        "Paiement en ligne sécurisé, suivi des paiements, historique complet par membre. Les notifications de rappel sont envoyées automatiquement à chaque adhérent concerné.",
    },
    {
      value: "fonds",
      icon: BarChart3,
      trigger: "Piloter votre fonds",
      content:
        "Tableau de bord en temps réel : montant collecté, membres à jour, projection annuelle. Transparence totale pour votre bureau et vos adhérents.",
    },
    {
      value: "communication",
      icon: MessageCircle,
      trigger: "Communiquer facilement",
      content:
        "Envoyez des messages à l'ensemble de vos membres en quelques clics. Rappels d'événements, actualités de l'association, annonces importantes depuis votre espace.",
    },
  ];

  const testimonials = [
    {
      quote:
        "Avant Hafani, je gérais 80 membres sur WhatsApp et Excel. Aujourd'hui tout est clair, les membres paient en ligne, et je peux montrer le fonds à l'assemblée.",
      author: "Moussa D.",
      role: "Président · Association · Paris",
      initial: "M",
      color: "bg-emerald-800",
    },
    {
      quote:
        "La gestion des paiements était notre plus grande difficulté. Avec Hafani, chaque paiement est enregistré automatiquement et le trésorier est informé immédiatement.",
      author: "Aminata K.",
      role: "Trésorière · Association · Lyon",
      initial: "A",
      color: "bg-teal-800",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#EAE8E4] font-sans text-slate-900 antialiased">

      {/* Photo fixe — visible à droite dès le chargement, se déploie en fond au scroll */}
      <div
        className="fixed inset-0 pointer-events-none hidden lg:block"
        style={{
          zIndex: 5,
          clipPath: `inset(0% 0% 0% ${(1 - photoReveal) * 52}% round ${(1 - photoReveal) * 28}px 0px 0px ${(1 - photoReveal) * 28}px)`,
          transition: "clip-path 0.06s linear",
        }}
      >
        <img
          src="/assets/hafani_association.png"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0 bg-slate-900/55"
          style={{ opacity: photoReveal }}
        />
      </div>

      <LandingHeader />

      <main className="flex-1">

        {/* ── 1. HERO — pleine page, split gauche/droite ── */}
        <section
          className="relative flex flex-col lg:flex-row min-h-screen"
          style={{ zIndex: 10 }}
        >
          {/* Colonne gauche — beige, texte */}
          <div className="w-full lg:w-[52%] bg-[#EAE8E4] flex flex-col px-8 sm:px-12 lg:px-16 xl:px-20 py-20 lg:py-0 lg:min-h-screen">

            {/* Contenu centré verticalement */}
            <div className="flex-1 flex flex-col justify-center py-8 space-y-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
                Hafani Management &amp; Solutions
              </p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-6xl xl:text-7xl text-slate-900 leading-[1.06] tracking-tight">
                La solidarité<br className="hidden sm:block" /> communautaire,{" "}
                <em className="not-italic text-emerald-700">enfin<br className="hidden sm:block" /> organisée.</em>
              </h1>
              <p className="text-base lg:text-lg text-slate-500 leading-relaxed max-w-sm font-light">
                Hafani MS vous accompagne dans la gestion de votre communauté. Créons votre espace dédié, gérons vos adhésions,
                suivons les cotisations et renforçons les liens de votre communauté.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-1 lg:hidden">
                <Button
                  onClick={() => scrollTo("demo")}
                  className="bg-slate-900 text-white hover:bg-emerald-800 rounded-full px-7 py-5 text-sm font-medium tracking-wide shadow-md transition-all duration-300 group w-fit"
                >
                  Demander une démo
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full px-7 py-5 text-sm border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all duration-300 w-fit"
                >
                  <Link to="/login">Rejoindre une communauté</Link>
                </Button>
              </div>
            </div>

            {/* Carte stats */}
            <div className="bg-white rounded-2xl px-6 py-5 shadow-sm border border-slate-100 flex flex-col sm:flex-row items-center gap-5 sm:gap-8 mb-14 lg:mb-24">
              <div className="flex items-center gap-4 flex-1">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                      Membres actifs
                    </span>
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                    </span>
                  </div>
                  <div className="font-serif text-3xl text-slate-900 tracking-tight">
                    {memberCount !== null ? (
                      <><span className="text-emerald-600">+</span>{memberCount}</>
                    ) : (
                      <span className="text-slate-300 animate-pulse">—</span>
                    )}
                  </div>
                </div>
                <div className="flex-1 hidden sm:block">
                  <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: "72%" }} />
                  </div>
                  <div className="mt-1.5 text-xs text-slate-400">72 % à jour cette année</div>
                </div>
              </div>
              <div className="hidden sm:block h-8 w-px bg-slate-100" />
              <div className="flex items-center gap-5 sm:gap-7">
                <div className="text-center">
                  <div className="font-serif text-2xl text-emerald-700">100%</div>
                  <div className="text-xs text-slate-400 mt-0.5 uppercase tracking-wide">Transparent</div>
                </div>
                <div className="h-7 w-px bg-slate-100" />
                <div className="text-center">
                  <div className="font-serif text-2xl text-slate-900">1</div>
                  <div className="text-xs text-slate-400 mt-0.5 uppercase tracking-wide">Association</div>
                </div>
              </div>
            </div>

          </div>

          {/* Colonne droite — desktop: transparent (photo fixe visible), mobile: photo */}
          <div className="flex-1 relative">
            <div className="lg:hidden w-full h-72 overflow-hidden">
              <img
                src="/assets/hafani_association.png"
                alt="Réunion d'association Hafani"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
          <div
            className="hidden lg:flex flex-col gap-3"
            style={{ position: "absolute", bottom: "26%", left: "56%", zIndex: 15 }}
          >
            <Button
              onClick={() => scrollTo("demo")}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-5 text-sm font-medium tracking-wide shadow-xl transition-all duration-300 group w-fit"
            >
              Demander une démo
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              asChild
              className="bg-white/12 backdrop-blur-md border border-white/30 text-white hover:bg-white/22 rounded-full px-7 py-5 text-sm font-medium tracking-wide transition-all duration-300 w-fit shadow-lg"
            >
              <Link to="/login">Rejoindre une communauté</Link>
            </Button>
          </div>
        </section>

        {/* ── 2. STATS BAND ── photo background fully revealed at this point */}
        <section ref={statsSectionRef} className="relative py-20" style={{ zIndex: 10 }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/20">
              <div className="py-8 md:py-4 px-8 text-center md:text-left">
                <div className="font-serif text-5xl text-white tracking-tight">
                  {memberCount !== null ? `+${memberCount}` : "—"}
                </div>
                <div className="text-xs font-semibold uppercase tracking-widest text-white/60 mt-2">
                  Membres actifs
                </div>
              </div>
              <div className="py-8 md:py-4 px-8 text-center">
                <div className="font-serif text-5xl text-white tracking-tight">1</div>
                <div className="text-xs font-semibold uppercase tracking-widest text-white/60 mt-2">
                  Association partenaire
                </div>
              </div>
              <div className="py-8 md:py-4 px-8 text-center md:text-right">
                <div className="font-serif text-5xl text-white tracking-tight">100%</div>
                <div className="text-xs font-semibold uppercase tracking-widest text-white/60 mt-2">
                  Transparence garantie
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. NOTRE ENGAGEMENT ── */}
        <section className="relative bg-[#EAE8E4] py-24 lg:py-32" style={{ zIndex: 10 }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

              {/* Left — Photo éditoriale */}
              <div className="relative hidden lg:block">
                <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-[4/3]">
                  <img
                    src="/assets/hafani_association.png"
                    alt="Communauté Hafani en réunion"
                    className="w-full h-full object-cover object-center"
                  />
                  {/* Overlay doux */}
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/20 via-transparent to-transparent pointer-events-none" />
                </div>
                {/* Badge flottant haut-gauche */}
                <div className="absolute -top-4 -right-4 bg-emerald-700 text-white rounded-2xl px-5 py-3 shadow-lg">
                  <div className="font-serif text-2xl leading-none">+{memberCount ?? "—"}</div>
                  <div className="text-xs text-emerald-200 mt-0.5 uppercase tracking-widest">membres</div>
                </div>
              </div>

              {/* Right — Texte + piliers */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-6">
                  Notre mission
                </p>
                <h2 className="font-serif text-5xl lg:text-6xl text-slate-900 leading-tight mb-8">
                  Un engagement de<br />
                  <em className="not-italic text-emerald-700">clarté et de transparence.</em>
                </h2>
                <p className="text-lg text-slate-700 leading-relaxed font-light mb-6">
                  Hafani MS est né d'une conviction simple : incarner la modernité de l'action solidaire.
                  Notre Mission est d'offrir une tranquilité d'esprit absolue à chaque membre de la communauté, en garantissant
                  une transparence totale, une solidarité structurée et une fiabilité professionnelle.
                </p>

                <div className="space-y-0 divide-y divide-slate-300/60 mt-10">
                  {[
                    {
                      icon: ShieldCheck,
                      title: "Transparence totale",
                      text: "Chaque euro collecté est tracé. Chaque membre peut voir où en est le fonds. Aucune zone d'ombre.",
                    },
                    {
                      icon: HeartHandshake,
                      title: "Solidarité structurée",
                      text: "Une solution qui garantie une gestion structurée.",
                    },
                    {
                      icon: Building2,
                      title: "Fiabilité professionnelle",
                      text: "Infrastructure sécurisée, données protégées, disponibilité 24h/24.",
                    },
                  ].map((pillar) => (
                    <div key={pillar.title} className="flex gap-5 py-7 group">
                      <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:bg-emerald-50 group-hover:border-emerald-200 transition-colors duration-300">
                        <pillar.icon className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{pillar.title}</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">{pillar.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. FONCTIONNALITÉS ── */}
        <section className="relative bg-white py-24 lg:py-32" style={{ zIndex: 10 }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">

              {/* Left — sticky title */}
              <div className="lg:sticky lg:top-24">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-6">
                  La plateforme
                </p>
                <h2 className="font-serif text-5xl lg:text-6xl text-slate-900 leading-tight mb-8">
                  Tout ce dont votre association a besoin.{" "}
                  <em className="not-italic text-emerald-700">À un seul endroit.</em>
                </h2>
                <p className="text-slate-600 leading-relaxed font-light mb-10">
                  Hafani MS centralise la gestion de votre association dans un espace unique,
                  sécurisé, accessible à tous vos membres.
                </p>
                <Button
                  onClick={() => scrollTo("demo")}
                  className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-full px-8 py-5 text-sm font-medium tracking-wide shadow-lg shadow-emerald-900/15 transition-all duration-300 group"
                >
                  Créer mon espace gratuitement
                  <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>

              {/* Right — Accordion */}
              <div>
                <Accordion type="single" collapsible defaultValue="membres" className="w-full">
                  {features.map((feat) => (
                    <AccordionItem
                      key={feat.value}
                      value={feat.value}
                      className="border-b border-slate-200 px-0"
                    >
                      <AccordionTrigger className="font-serif text-2xl lg:text-3xl text-slate-900 hover:text-emerald-700 hover:no-underline py-7 data-[state=open]:text-emerald-700 transition-colors duration-200 group">
                        <span className="flex items-center gap-4">
                          <span className="h-9 w-9 rounded-lg bg-slate-100 group-data-[state=open]:bg-emerald-50 flex items-center justify-center flex-shrink-0 transition-colors duration-200">
                            <feat.icon className="h-4 w-4 text-slate-500 group-data-[state=open]:text-emerald-600 transition-colors duration-200" />
                          </span>
                          {feat.trigger}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 leading-relaxed pb-7 pl-[52px] font-light text-base">
                        {feat.content}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        </section>

        {/* ── 5. APPELS À L'ACTION ── */}
        <section className="relative bg-slate-900 py-24 lg:py-32" style={{ zIndex: 10 }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400 mb-4">
                Par où commencer ?
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
                Vous aussi, adoptez nos solutions.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">

              {/* Card 1 — Particulier */}
              <div className="group relative bg-slate-800 hover:bg-emerald-900 border border-slate-700 hover:border-emerald-700 rounded-3xl p-10 flex flex-col justify-between transition-all duration-500 cursor-pointer overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-400/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                <div>
                  <div className="h-14 w-14 rounded-2xl bg-slate-700 group-hover:bg-emerald-800 flex items-center justify-center mb-8 transition-colors duration-300">
                    <Users className="h-7 w-7 text-emerald-400" />
                  </div>
                  <h3 className="font-serif text-3xl text-white mb-4 leading-tight">
                    Je rejoins<br />une communauté
                  </h3>
                  <p className="text-slate-400 group-hover:text-slate-300 text-sm leading-relaxed transition-colors duration-300 font-light">
                    Votre contribution au Hafani est essentielle pour garantir la pérennité
                    de l'action de solidarité. Rejoignez le mouvement dès aujourd'hui et
                    participez à l'effort collectif.
                  </p>
                </div>
                <div className="mt-10">
                  <Button
                    asChild
                    className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full px-7 py-5 text-sm font-medium tracking-wide transition-all duration-300 w-full justify-center"
                  >
                    <Link to="/login">
                      Trouver une communauté
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Card 2 — Association */}
              <div className="group relative bg-emerald-700 hover:bg-emerald-600 border border-emerald-600 hover:border-emerald-500 rounded-3xl p-10 flex flex-col justify-between transition-all duration-500 cursor-pointer overflow-hidden">
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                <div>
                  <div className="h-14 w-14 rounded-2xl bg-emerald-600 group-hover:bg-emerald-500 flex items-center justify-center mb-8 transition-colors duration-300">
                    <Building2 className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="font-serif text-3xl text-white mb-4 leading-tight">
                    J'inscris<br />ma communauté
                  </h3>
                  <p className="text-emerald-100 group-hover:text-white text-sm leading-relaxed transition-colors duration-300 font-light">
                    Créez votre espace dédié, personnalisez-le et invitez vos membres à vous rejoindre en
                    quelques clics.
                  </p>
                </div>
                <div className="mt-10">
                  <Button
                    onClick={() => scrollTo("demo")}
                    className="bg-white text-emerald-900 hover:bg-emerald-50 rounded-full px-7 py-5 text-sm font-medium tracking-wide transition-all duration-300 w-full justify-center"
                  >
                    Créer mon espace
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 6. TÉMOIGNAGES ── */}
        <section className="relative bg-[#EAE8E4] py-24 lg:py-32" style={{ zIndex: 10 }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-16">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-4">
                Ils témoignent
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-slate-900">
                "Seul on va plus vite,<br className="hidden sm:block" /> ensemble on va plus loin."
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.author}
                  className="relative bg-white rounded-2xl p-10 border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-emerald-500 text-emerald-500" />
                    ))}
                  </div>
                  <blockquote className="font-serif text-xl lg:text-2xl text-slate-800 leading-relaxed mb-8 italic">
                    "{t.quote}"
                  </blockquote>
                  <div className="flex items-center gap-4 pt-6 border-t border-slate-100">
                    <div className={`h-11 w-11 rounded-full ${t.color} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}>
                      {t.initial}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{t.author}</div>
                      <div className="text-slate-400 text-xs mt-0.5">{t.role}</div>
                    </div>
                    <div className="ml-auto text-slate-300 text-xs uppercase tracking-widest font-medium">
                      [À venir]
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 7. FORMULAIRE DÉMO ── */}
        <section id="demo" className="relative bg-white py-24 lg:py-32" style={{ zIndex: 10 }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">

              {/* Left */}
              <div className="lg:sticky lg:top-24">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-6">
                  Passez à l'action
                </p>
                <h2 className="font-serif text-5xl lg:text-6xl text-slate-900 leading-tight mb-8">
                  Lançons votre association{" "}
                  <em className="not-italic text-emerald-700">ensemble.</em>
                </h2>
                <p className="text-slate-600 leading-relaxed font-light mb-10 text-lg">
                  Remplissez ce formulaire et un membre de notre équipe vous contactera
                  sous 48h pour configurer votre espace Hafani personnalisé.
                </p>

                <div className="space-y-5">
                  {[
                    "Création de votre espace : gratuit",
                    "Mise en place accompagnée sous 48h",
                    "Aucun engagement à la signature",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-slate-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — Form */}
              <div className="bg-[#EAE8E4] rounded-3xl p-8 lg:p-10">
                {formStatus === "success" ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
                      <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h3 className="font-serif text-2xl text-slate-900 mb-3">
                      Demande envoyée !
                    </h3>
                    <p className="text-slate-600 max-w-xs font-light">
                      Notre équipe vous contactera dans les 48h pour configurer votre
                      espace Hafani.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-1.5">
                        <Label htmlFor="firstName" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                          Prénom <span className="text-red-400 normal-case">*</span>
                        </Label>
                        <Input
                          id="firstName" name="firstName" required
                          placeholder="Votre prénom"
                          value={form.firstName} onChange={handleFormChange}
                          className="h-12 rounded-xl border-slate-300 bg-white focus-visible:ring-emerald-500 text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="lastName" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                          Nom <span className="text-red-400 normal-case">*</span>
                        </Label>
                        <Input
                          id="lastName" name="lastName" required
                          placeholder="Votre nom"
                          value={form.lastName} onChange={handleFormChange}
                          className="h-12 rounded-xl border-slate-300 bg-white focus-visible:ring-emerald-500 text-sm"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        Email <span className="text-red-400 normal-case">*</span>
                      </Label>
                      <Input
                        id="email" name="email" type="email" required
                        placeholder="votre@email.com"
                        value={form.email} onChange={handleFormChange}
                        className="h-12 rounded-xl border-slate-300 bg-white focus-visible:ring-emerald-500 text-sm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        Téléphone
                      </Label>
                      <Input
                        id="phone" name="phone" type="tel"
                        placeholder="+33 6 XX XX XX XX"
                        value={form.phone} onChange={handleFormChange}
                        className="h-12 rounded-xl border-slate-300 bg-white focus-visible:ring-emerald-500 text-sm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="associationName" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        Nom de l'association <span className="text-red-400 normal-case">*</span>
                      </Label>
                      <Input
                        id="associationName" name="associationName" required
                        placeholder="Association [Ville]"
                        value={form.associationName} onChange={handleFormChange}
                        className="h-12 rounded-xl border-slate-300 bg-white focus-visible:ring-emerald-500 text-sm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="memberEstimate" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        Nombre de membres (estimation)
                      </Label>
                      <select
                        id="memberEstimate" name="memberEstimate"
                        value={form.memberEstimate} onChange={handleFormChange}
                        className="w-full h-12 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="">Sélectionner une tranche</option>
                        <option value="< 20">&lt; 20</option>
                        <option value="20-50">20 – 50</option>
                        <option value="50-100">50 – 100</option>
                        <option value="100+">100+</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="message" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        Message / Question
                      </Label>
                      <Textarea
                        id="message" name="message"
                        placeholder="Décrivez votre situation actuelle..."
                        value={form.message} onChange={handleFormChange}
                        className="rounded-xl border-slate-300 bg-white focus-visible:ring-emerald-500 min-h-[110px] resize-none text-sm"
                      />
                    </div>

                    {formStatus === "error" && (
                      <p className="text-red-500 text-sm text-center">
                        Une erreur est survenue. Réessayez ou contactez-nous directement.
                      </p>
                    )}

                    <Button
                      type="submit"
                      disabled={formStatus === "loading"}
                      className="w-full bg-slate-900 text-white hover:bg-emerald-800 rounded-full py-6 text-sm font-medium tracking-wide shadow-lg transition-all duration-300 disabled:opacity-60 group"
                    >
                      {formStatus === "loading" ? "Envoi en cours…" : (
                        <>
                          Envoyer ma demande
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

      </main>

      <div className="relative" style={{ zIndex: 10 }}>
        <Footer homeLink="/" contactLink="/contact" />
      </div>
    </div>
  );
};
